import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring, useMotionValue, useVelocity, useAnimationFrame } from "framer-motion";
import { wrap } from "@motionone/utils";

interface ParallaxTextProps {
    children: string;
    baseVelocity: number;
}

function ParallaxText({ children, baseVelocity = 100 }: ParallaxTextProps) {
    const baseX = useMotionValue(0);
    const { scrollY } = useScroll();
    const scrollVelocity = useVelocity(scrollY);
    const smoothVelocity = useSpring(scrollVelocity, {
        damping: 50,
        stiffness: 400
    });

    const velocityFactor = useTransform(smoothVelocity, [0, 1000], [0, 5], {
        clamp: false
    });

    /**
     * This is a magic wrapping for the length of the text - you
     * have to replace for wrapping that works for you or dynamically
     * calculate
     */
    const x = useTransform(baseX, (v) => `${wrap(-20, -45, v)}%`);

    const directionFactor = useRef<number>(1);
    useAnimationFrame((t, delta) => {
        let moveBy = directionFactor.current * baseVelocity * (delta / 1000);

        if (velocityFactor.get() < 0) {
            directionFactor.current = -1;
        } else if (velocityFactor.get() > 0) {
            directionFactor.current = 1;
        }

        moveBy += directionFactor.current * moveBy * velocityFactor.get();

        baseX.set(baseX.get() + moveBy);
    });

    return (
        <div className="parallax overflow-hidden m-0 whitespace-nowrap flex flex-nowrap">
            <motion.div className="scroller font-black uppercase text-9xl flex whitespace-nowrap flex-nowrap items-center gap-10" style={{ x }}>
                <span>{children} </span>
                <span>{children} </span>
                <span>{children} </span>
                <span>{children} </span>
            </motion.div>
        </div>
    );
}

const testimonials = [
    { name: "Yohane H. M.", text: "Logistics Redefined." },
    { name: "Sarah M.", text: "Unimaginably Fast." },
    { name: "K&C Logistics", text: "The Future is Now." },
    { name: "Transport Co.", text: "Seamless Integration." },
];

const MarqueeTestimonials = () => {
    return (
        <section className="py-24 bg-background overflow-hidden relative z-20">
            <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-background to-transparent z-10" />
            <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-background to-transparent z-10" />

            <div className="space-y-12 opacity-80 hover:opacity-100 transition-opacity duration-500">
                <ParallaxText baseVelocity={-2}>Streamlined Efficiency</ParallaxText>
                <ParallaxText baseVelocity={2}>Global Standards Local Reach</ParallaxText>
                <ParallaxText baseVelocity={-1.5}>Trusted By Industry Leaders</ParallaxText>
            </div>

            <div className="container mx-auto mt-24">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {testimonials.map((t, i) => (
                        <div key={i} className="p-8 border border-white/10 rounded-3xl bg-white/5 backdrop-blur-sm hover:bg-white/10 transition-colors">
                            <p className="text-2xl font-bold mb-4">"{t.text}"</p>
                            <p className="text-muted-foreground">- {t.name}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default MarqueeTestimonials;
